// Naplánované úlohy v nastavení: seznam, založení a úprava, zapnutí, „Spustit teď“. Úlohu jde zadat i v chatu.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClockIcon, DotsThreeIcon, PencilSimpleIcon, PlayIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Button, DropdownMenu, EmptyState, Input, Select, Spinner, Switch, Textarea, useConfirm, useToast } from '@smworks-cz/ui-kit';
import { describeSchedule, WEEKDAY_NAMES, type ScheduleDTO, type ScheduleFreq } from '@sam/shared';
import { api } from '../lib/api';
import { keys, queryClient } from '../lib/queries';
import { chatPath, navigate } from '../lib/router';
import { fmtDateTime } from '../lib/format';

const KEY = ['schedules'];
const FREQ_OPTIONS = [
  { value: 'daily', label: 'Každý den' },
  { value: 'weekdays', label: 'V pracovní dny' },
  { value: 'weekly', label: 'Jednou týdně' },
  { value: 'once', label: 'Jednou (v daný den a čas)' },
];

/** Hodnota pro <input type="datetime-local"> (místní čas prohlížeče). */
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const DAY_OPTIONS = WEEKDAY_NAMES.map((d, i) => ({ value: String(i + 1), label: d[0].toUpperCase() + d.slice(1) }));

const EXAMPLES = [
  { title: 'Ranní přehled', prompt: 'Připrav mi ranní přehled: co mám dnes v kalendáři, úkoly s termínem dnes a po termínu a jak jsem na tom s výkazy tento týden.', freq: 'weekdays' as const, time: '09:00' },
  { title: 'Shrnutí týdne', prompt: 'Shrň můj týden: kolik hodin jsem vykázal a na čem, které úkoly jsem dokončil a co mi zbývá na příští týden.', freq: 'weekly' as const, weekday: 5, time: '15:00' },
];

export function SchedulesTab({ onClose }: { onClose: () => void }) {
  const q = useQuery({ queryKey: KEY, queryFn: async () => (await api<{ schedules: ScheduleDTO[] }>('GET', '/schedules')).schedules, staleTime: 0 });
  const [editing, setEditing] = useState<Partial<ScheduleDTO> | null>(null);
  const confirm = useConfirm();
  const { toast } = useToast();
  const fail = (e: unknown) => toast({ variant: 'error', title: (e as Error).message });

  const patch = async (s: ScheduleDTO, p: Partial<ScheduleDTO>) => {
    try {
      const r = await api<{ schedule: ScheduleDTO }>('PATCH', `/schedules/${s.id}`, p);
      queryClient.setQueryData<ScheduleDTO[]>(KEY, (old) => old?.map((x) => (x.id === s.id ? r.schedule : x)));
    } catch (e) {
      fail(e);
    }
  };
  const remove = async (s: ScheduleDTO) => {
    const ok = await confirm({ title: `Smazat úlohu „${s.title}“?`, description: 'Chaty z dřívějších spuštění zůstanou.', confirmLabel: 'Smazat', cancelLabel: 'Zrušit', variant: 'danger' });
    if (!ok) return;
    try {
      await api('DELETE', `/schedules/${s.id}`);
      queryClient.setQueryData<ScheduleDTO[]>(KEY, (old) => old?.filter((x) => x.id !== s.id));
    } catch (e) {
      fail(e);
    }
  };
  const runNow = async (s: ScheduleDTO) => {
    try {
      const r = await api<{ conversationId: string }>('POST', `/schedules/${s.id}/run`);
      void queryClient.invalidateQueries({ queryKey: keys.conversations });
      void queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
      navigate(chatPath(r.conversationId));
    } catch (e) {
      fail(e);
    }
  };

  return (
    <div className="set-list">
      <p className="muted">
        Sam v daný čas sám udělá, co mu zadáte, a výsledek pošle do nového chatu (v menu ho poznáte podle hodin a tečky, dokud ho neotevřete). Běží s vašimi
        právy. Úlohu můžete zadat i v chatu, třeba „každý pracovní den v 9 mi napiš ranní přehled“.
      </p>
      {!editing && (
        <div>
          <Button size="sm" icon={<PlusIcon size={15} />} onClick={() => setEditing({ freq: 'weekdays', time: '09:00', weekday: null })}>
            Nová úloha
          </Button>
        </div>
      )}
      {editing && <ScheduleForm initial={editing} onDone={() => setEditing(null)} />}
      {q.isLoading && <Spinner />}
      {q.error && <p className="bad">{(q.error as Error).message}</p>}
      {q.data && !q.data.length && !editing && (
        <div className="sch-examples">
          <EmptyState size="sm" title="Zatím žádné úlohy" description="Začněte třeba jednou z těchto:" />
          {EXAMPLES.map((x) => (
            <button key={x.title} type="button" className="sch-example" onClick={() => setEditing({ ...x, weekday: x.weekday ?? null })}>
              <b>{x.title}</b>
              <small>{describeSchedule({ freq: x.freq, weekday: x.weekday ?? null, time: x.time })}</small>
            </button>
          ))}
        </div>
      )}
      {q.data?.map((s) => (
        <div key={s.id} className={`sch-row${s.enabled ? '' : ' off'}`}>
          <ClockIcon size={18} className="sch-ico" />
          <div className="sch-t">
            <b>{s.title}</b>
            <small>
              {describeSchedule(s)}
              {s.enabled && s.nextRunAt ? ` · příště ${fmtDateTime(s.nextRunAt)}` : s.enabled ? '' : s.freq === 'once' && s.lastRunAt ? ' · proběhla' : ' · vypnuto'}
            </small>
            {s.lastRunAt && (
              <small className={s.lastStatus === 'error' ? 'bad' : undefined}>
                naposledy {fmtDateTime(s.lastRunAt)}
                {s.lastStatus === 'error' ? `: ${s.lastError ?? 'chyba'}` : ''}
                {s.lastConversationId && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        onClose();
                        navigate(chatPath(s.lastConversationId!));
                      }}
                    >
                      otevřít výsledek
                    </button>
                  </>
                )}
              </small>
            )}
          </div>
          <Switch size="sm" checked={s.enabled} onChange={(on) => void patch(s, { enabled: on })} aria-label={`Zapnout úlohu ${s.title}`} />
          <DropdownMenu
            position="bottom-right"
            trigger={
              <button type="button" className="nav-more adm-more" aria-label={`Akce pro úlohu ${s.title}`}>
                <DotsThreeIcon size={18} weight="bold" />
              </button>
            }
            items={[
              { label: 'Spustit teď', icon: <PlayIcon size={16} />, onClick: () => void runNow(s) },
              { label: 'Upravit', icon: <PencilSimpleIcon size={16} />, onClick: () => setEditing(s) },
              { label: 'Smazat', icon: <TrashIcon size={16} />, danger: true, onClick: () => void remove(s) },
            ]}
          />
        </div>
      ))}
    </div>
  );
}

function ScheduleForm({ initial, onDone }: { initial: Partial<ScheduleDTO>; onDone: () => void }) {
  const [title, setTitle] = useState(initial.title ?? '');
  const [prompt, setPrompt] = useState(initial.prompt ?? '');
  const [freq, setFreq] = useState<ScheduleFreq>(initial.freq ?? 'weekdays');
  const [weekday, setWeekday] = useState<number>(initial.weekday ?? 1);
  const [time, setTime] = useState(initial.time ?? '09:00');
  const [runAt, setRunAt] = useState(toLocalInput(initial.runAt ? new Date(initial.runAt) : new Date(Date.now() + 60 * 60_000)));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const save = async () => {
    setSaving(true);
    const at = freq === 'once' ? new Date(runAt) : null;
    const body = {
      title: title.trim(),
      prompt: prompt.trim(),
      freq,
      weekday: freq === 'weekly' ? weekday : null,
      time: at ? `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}` : time,
      runAt: at ? at.toISOString() : null,
    };
    try {
      if (initial.id) await api('PATCH', `/schedules/${initial.id}`, body);
      else await api('POST', '/schedules', body);
      void queryClient.invalidateQueries({ queryKey: KEY });
      toast({ variant: 'success', title: initial.id ? 'Úloha upravena.' : `Úloha naplánována: ${describeSchedule(body)}.` });
      onDone();
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className="sch-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <Input label="Název" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ranní přehled" required maxLength={80} />
      <Textarea
        label="Co má Sam udělat"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Připrav mi ranní přehled: kalendář na dnes, úkoly s termínem a výkazy za týden."
        rows={3}
        required
      />
      <div className="sch-when">
        <div className="adm-add-role">
          <span className="adm-label">Kdy</span>
          <Select options={FREQ_OPTIONS} value={freq} onChange={(v) => setFreq(v as ScheduleFreq)} aria-label="Jak často" />
        </div>
        {freq === 'weekly' && (
          <div className="adm-add-role">
            <span className="adm-label">Den</span>
            <Select options={DAY_OPTIONS} value={String(weekday)} onChange={(v) => setWeekday(Number(v))} aria-label="Den v týdnu" />
          </div>
        )}
        {freq === 'once' ? (
          <Input label="Kdy" type="datetime-local" value={runAt} onChange={(e) => setRunAt(e.target.value)} required />
        ) : (
          <Input label="Čas" type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
        )}
      </div>
      <div className="adm-add-actions">
        <Button variant="outline" size="sm" type="button" onClick={onDone}>
          Zrušit
        </Button>
        <Button size="sm" type="submit" loading={saving} disabled={!title.trim() || prompt.trim().length < 3 || (freq === 'once' ? !runAt : !/^\d{2}:\d{2}$/.test(time))}>
          {initial.id ? 'Uložit' : 'Naplánovat'}
        </Button>
      </div>
    </form>
  );
}
